import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  DateRange as DateRangeIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Auth helper function
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    withCredentials: true
  };
};

function DailySummary({ user }) {
  // Date mode: 'single' for single date, 'range' for date range, 'ason' for cumulative as-on date
  const [dateMode, setDateMode] = useState('single');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().split('T')[0]);
  
  const [summaries, setSummaries] = useState(null);
  const [shiftSummaries, setShiftSummaries] = useState(null);
  const [editInfo, setEditInfo] = useState(null);
  const [grandTotals, setGrandTotals] = useState(null);
  const [dataSource, setDataSource] = useState('');
  const [dateRangeLabel, setDateRangeLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  
  // Lock state (read-only - auto-lock system)
  const [isLocked, setIsLocked] = useState(false);
  const [lockedBy, setLockedBy] = useState(null);
  const [lockedAt, setLockedAt] = useState(null);
  
  // Edit history dialog
  const [editHistoryOpen, setEditHistoryOpen] = useState(false);
  const [editHistoryData, setEditHistoryData] = useState(null);

  const fetchDailySummary = async () => {
    let apiUrl = '';
    let label = '';
    
    // Build API URL based on date mode
    if (dateMode === 'single') {
      if (!selectedDate) {
        setError('Please select a date');
        return;
      }
      apiUrl = `https://ccc4cs.choicegroup.in/api/daily_summary_v2.php?date=${selectedDate}`;
      label = `Date: ${selectedDate}`;
    } else if (dateMode === 'range') {
      if (!dateFrom || !dateTo) {
        setError('Please select both From and To dates');
        return;
      }
      if (dateFrom > dateTo) {
        setError('From date cannot be after To date');
        return;
      }
      apiUrl = `https://ccc4cs.choicegroup.in/api/daily_summary_v2.php?date_from=${dateFrom}&date_to=${dateTo}`;
      label = `Range: ${dateFrom} to ${dateTo}`;
    } else if (dateMode === 'ason') {
      if (!asOnDate) {
        setError('Please select As On date');
        return;
      }
      apiUrl = `https://ccc4cs.choicegroup.in/api/daily_summary_v2.php?as_on_date=${asOnDate}`;
      label = `As On: ${asOnDate} (Cumulative)`;
    }

    try {
      setLoading(true);
      setError('');
      setSuccessMsg('');
      setDateRangeLabel(label);

      const response = await axios.get(apiUrl, getAuthConfig());

      if (response.data.success) {
        setSummaries(response.data.summaries);
        setShiftSummaries(response.data.shift_summaries);
        setEditInfo(response.data.edit_info || {});
        setGrandTotals(response.data.grand_totals || {});
        setDataSource(response.data.source || 'live');
        
        // Set lock status
        setIsLocked(response.data.is_locked || false);
        setLockedBy(response.data.locked_by || null);
        setLockedAt(response.data.locked_at || null);
      } else {
        setError(response.data.error || 'Failed to load summary');
      }
    } catch (err) {
      console.error('Failed to fetch daily summary:', err);
      setError('Failed to load daily summary');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchDailySummary();
  };

  const handleDateModeChange = (event, newMode) => {
    if (newMode !== null) {
      setDateMode(newMode);
      setSummaries(null);
      setShiftSummaries(null);
      setGrandTotals(null);
      setDateRangeLabel('');
      setIsLocked(false);
      setLockedBy(null);
      setLockedAt(null);
    }
  };

  // Quick date presets
  const setQuickDate = (preset) => {
    const today = new Date();
    let from, to;
    
    switch (preset) {
      case 'today':
        from = to = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        from = to = yesterday.toISOString().split('T')[0];
        break;
      case 'last7':
        to = today.toISOString().split('T')[0];
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 6);
        from = last7.toISOString().split('T')[0];
        break;
      case 'last30':
        to = today.toISOString().split('T')[0];
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 29);
        from = last30.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        to = today.toISOString().split('T')[0];
        from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        break;
      case 'lastMonth':
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        from = lastMonthStart.toISOString().split('T')[0];
        to = lastMonthEnd.toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    if (dateMode === 'single') {
      setSelectedDate(from);
    } else if (dateMode === 'range') {
      setDateFrom(from);
      setDateTo(to);
    } else if (dateMode === 'ason') {
      setAsOnDate(from);
    }
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return Number(num).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  // Render summary table for a category
  const renderSummaryTable = (category, data, totals) => {
    if (!data || data.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
          No data for {category}
        </Typography>
      );
    }

    const showStatus = category === 'REPACK-IN' || category === 'REPACK-OUT';

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: category === 'PRODUCED' ? '#e8f5e9' : category === 'REPACK-IN' ? '#e3f2fd' : '#fff3e0' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Shift</TableCell>
              {showStatus && <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>}
              <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Grade</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Pack Size</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Brand</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Cartons</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Loose Bags</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Loose KGs</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total KGs</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Pounds</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  <Chip 
                    label={row.shift} 
                    size="small" 
                    color={row.shift === 'DAY' ? 'warning' : row.shift === 'NIGHT' ? 'primary' : 'default'}
                  />
                </TableCell>
                {showStatus && <TableCell>{row.status}</TableCell>}
                <TableCell>{row.product}</TableCell>
                <TableCell>{row.grade}</TableCell>
                <TableCell>{row.pack_size}</TableCell>
                <TableCell>{row.brand}</TableCell>
                <TableCell align="right">{formatNumber(row.total_cartons)}</TableCell>
                <TableCell align="right" sx={{ color: row.total_loose_bags > 0 ? 'green' : 'inherit' }}>
                  {formatNumber(row.total_loose_bags)}
                </TableCell>
                <TableCell align="right" sx={{ color: row.total_loose_kgs > 0 ? 'green' : 'inherit' }}>
                  {formatNumber(row.total_loose_kgs)}
                </TableCell>
                <TableCell align="right" sx={{ color: 'primary.main' }}>{formatNumber(row.total_kgs)}</TableCell>
                <TableCell align="right" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                  {formatNumber(row.total_pounds)}
                </TableCell>
              </TableRow>
            ))}
            {/* Grand Total Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell colSpan={showStatus ? 6 : 5} sx={{ fontWeight: 'bold' }}>GRAND TOTAL:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(totals?.total_cartons)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: 'green' }}>{formatNumber(totals?.total_loose_bags)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: 'green' }}>{formatNumber(totals?.total_loose_kgs)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatNumber(totals?.total_kgs)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>{formatNumber(totals?.total_pounds)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📊 Daily Summary
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          View consolidated summary with shift-wise breakdown (grouped by Shift, Product, Grade, Pack Size, and Brand)
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

        {/* Date Mode Toggle */}
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={dateMode}
            exclusive
            onChange={handleDateModeChange}
            size="small"
          >
            <ToggleButton value="single">
              <TodayIcon sx={{ mr: 1 }} /> SINGLE DATE
            </ToggleButton>
            <ToggleButton value="range">
              <DateRangeIcon sx={{ mr: 1 }} /> DATE RANGE
            </ToggleButton>
            <ToggleButton value="ason">
              <CalendarIcon sx={{ mr: 1 }} /> AS ON DATE
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Quick Date Presets */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" onClick={() => setQuickDate('today')}>TODAY</Button>
          <Button variant="outlined" size="small" onClick={() => setQuickDate('yesterday')}>YESTERDAY</Button>
          <Button variant="outlined" size="small" onClick={() => setQuickDate('last7')}>LAST 7 DAYS</Button>
          <Button variant="outlined" size="small" onClick={() => setQuickDate('last30')}>LAST 30 DAYS</Button>
          <Button variant="outlined" size="small" onClick={() => setQuickDate('thisMonth')}>THIS MONTH</Button>
          <Button variant="outlined" size="small" onClick={() => setQuickDate('lastMonth')}>LAST MONTH</Button>
        </Box>

        {/* Date Inputs */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {dateMode === 'single' && (
            <Grid item>
              <TextField
                label="Select Date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          )}
          {dateMode === 'range' && (
            <>
              <Grid item>
                <TextField
                  label="From Date"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item>
                <TextField
                  label="To Date"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
            </>
          )}
          {dateMode === 'ason' && (
            <Grid item>
              <TextField
                label="As On Date"
                type="date"
                value={asOnDate}
                onChange={(e) => setAsOnDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          )}
          <Grid item>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              SEARCH SUMMARY
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              REFRESH
            </Button>
          </Grid>
        </Grid>

        {/* Lock Status Display (Auto-lock - no manual button) */}
        {dateMode === 'single' && summaries && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {isLocked ? (
              <>
                <Chip
                  icon={<LockIcon />}
                  label={`🔒 AUTO-LOCKED on ${new Date(lockedAt).toLocaleString()}`}
                  color="success"
                  variant="filled"
                  sx={{ fontWeight: 'bold' }}
                />
                <Chip label="📁 Viewing: Frozen Snapshot Data" color="info" size="small" />
                <Typography variant="caption" color="textSecondary">
                  (Locked by: {lockedBy})
                </Typography>
              </>
            ) : (
              <>
                <Chip
                  icon={<LockOpenIcon />}
                  label="🔓 NOT LOCKED (Live Data)"
                  color="warning"
                  variant="outlined"
                />
                <Chip label="📊 Viewing: Live Calculated Data" color="default" size="small" />
                <Typography variant="caption" color="textSecondary">
                  (Will auto-lock when next day's DAY shift entry is made)
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Date Range Label */}
        {dateRangeLabel && !loading && (
          <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
            📅 Showing: {dateRangeLabel}
          </Typography>
        )}

        {/* No Data Message */}
        {!loading && !summaries && (
          <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', p: 3 }}>
            Select a date and click "Search Summary" to view data
          </Typography>
        )}

        {/* Summary Accordions */}
        {summaries && (
          <Box>
            {/* PRODUCED */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#e8f5e9' }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  PRODUCED ({summaries['PRODUCED']?.count || 0} groups)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                  <Chip label={`Cartons: ${formatNumber(grandTotals?.['PRODUCED']?.cartons)}`} size="small" color="primary" />
                  <Chip label={`Loose Bags: ${formatNumber(grandTotals?.['PRODUCED']?.loose_bags)}`} size="small" color="success" />
                  <Chip label={`KGs: ${formatNumber(grandTotals?.['PRODUCED']?.kgs)}`} size="small" color="info" />
                  <Chip label={`LBs: ${formatNumber(grandTotals?.['PRODUCED']?.pounds)}`} size="small" color="secondary" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderSummaryTable('PRODUCED', summaries['PRODUCED']?.data, summaries['PRODUCED']?.totals)}
              </AccordionDetails>
            </Accordion>

            {/* REPACK-IN */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#e3f2fd' }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  REPACK-IN ({summaries['REPACK-IN']?.count || 0} groups)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                  <Chip label={`Cartons: ${formatNumber(grandTotals?.['REPACK-IN']?.cartons)}`} size="small" color="primary" />
                  <Chip label={`Loose Bags: ${formatNumber(grandTotals?.['REPACK-IN']?.loose_bags)}`} size="small" color="success" />
                  <Chip label={`KGs: ${formatNumber(grandTotals?.['REPACK-IN']?.kgs)}`} size="small" color="info" />
                  <Chip label={`LBs: ${formatNumber(grandTotals?.['REPACK-IN']?.pounds)}`} size="small" color="secondary" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderSummaryTable('REPACK-IN', summaries['REPACK-IN']?.data, summaries['REPACK-IN']?.totals)}
              </AccordionDetails>
            </Accordion>

            {/* REPACK-OUT */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#fff3e0' }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  REPACK-OUT ({summaries['REPACK-OUT']?.count || 0} groups)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                  <Chip label={`Cartons: ${formatNumber(grandTotals?.['REPACK-OUT']?.cartons)}`} size="small" color="primary" />
                  <Chip label={`Loose Bags: ${formatNumber(grandTotals?.['REPACK-OUT']?.loose_bags)}`} size="small" color="success" />
                  <Chip label={`KGs: ${formatNumber(grandTotals?.['REPACK-OUT']?.kgs)}`} size="small" color="info" />
                  <Chip label={`LBs: ${formatNumber(grandTotals?.['REPACK-OUT']?.pounds)}`} size="small" color="secondary" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderSummaryTable('REPACK-OUT', summaries['REPACK-OUT']?.data, summaries['REPACK-OUT']?.totals)}
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Paper>

    </Box>
  );
}

export default DailySummary;